import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { Absence } from "./models/Absence";
import {
  createAbsence,
  deleteAbsence,
  updateAbsence,
} from "./slice-extensions/absences-slice-extension";
import { useClosedPeriodBridge } from "./use-closed-period-bridge";

/**
 * Absences-page controller: holds the record/edit dialog state, runs the save
 * and delete actions with their toasts, and hands a fresh creation to the
 * closed-period bridge (the billing follow-up offer for subscription members).
 */
export const useAbsenceActions = (memberNameById: Map<string, string>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [dialog, setDialog] = useState<{ absence?: Absence } | null>(null);
  const [busy, setBusy] = useState(false);
  const bridge = useClosedPeriodBridge();

  const submit = async (memberId: string, startDate: string, endDate: string, note: string) => {
    setBusy(true);
    const result = dialog?.absence
      ? await dispatch(
          updateAbsence({ id: dialog.absence.id, request: { startDate, endDate, note } }),
        )
      : await dispatch(createAbsence({ memberId, startDate, endDate, note: note || null }));
    setBusy(false);
    if (createAbsence.fulfilled.match(result) || updateAbsence.fulfilled.match(result)) {
      toast.success(t("absences.toastSaved", "Absence saved."));
      setDialog(null);
      if (createAbsence.fulfilled.match(result)) {
        // Subscription members get a one-click billing follow-up (admins only).
        void bridge.maybeOffer({
          memberId,
          memberName: memberNameById.get(memberId) ?? "",
          startDate,
          endDate,
          note,
        });
      }
    } else {
      toast.error(result.payload?.message ?? t("absences.saveFailed", "Failed to save the absence."));
    }
  };

  const remove = async (absence: Absence) => {
    const result = await dispatch(deleteAbsence(absence.id));
    if (deleteAbsence.fulfilled.match(result)) {
      toast.success(t("absences.toastDeleted", "Absence deleted."));
    } else {
      toast.error(result.payload?.message ?? t("absences.deleteFailed", "Failed to delete the absence."));
    }
  };

  return { dialog, setDialog, busy, bridge, submit, remove };
};
